<!-- 
  SVG Icon Component
  
  This component is used to display SVG icons.

  - The SVG icons are loaded asynchronously from the server and inserted into the document.
  - The SVG icons are loaded based on the folder and name provided in the props.
  - The folder is used to determine the sprite file to load, 
    and the name is used to determine the specific icon to display.
  - The sprite file is loaded only once per folder, and the icon is displayed once the sprite is loaded.
  - The sprite status is stored in the state to prevent multiple AJAX requests for the same sprite.
-->
<template>
  <span>
    <svg
      v-if="
        (useState(spriteStatusKey).value as SpriteStatusMap)?.[props.folder] ===
        SpriteStatus.LOADED
      "
      :aria-hidden="ariaHidden === 'false' ? false : true"
    >
      <title v-if="title">{{ title }}</title>
      <use :href="`#${props.folder}-${name}`"></use>
    </svg>
  </span>
</template>

<script lang="ts" setup>
import { useState } from 'nuxt/app';
/**
 * PROPERTIES / PROPS
 * (These are variables that are passed to the component from the parent component)
 */
const props = defineProps<{
  folder: string;
  name: string;
  title?: string;
  ariaHidden?: string;
}>();

/**
 * Status of the sprite - loading or loaded
 */
enum SpriteStatus {
  LOADING = 'loading',
  LOADED = 'loaded'
}

/**
 * INITIALIZE COMPOSABLES / CONSTS / PRIVATE FUNCTIONS ? STORE
 * (Helpers for rendering the template)
 */

const runtimeConfig = useRuntimeConfig();
const spriteStatusKey = 'spriteStatus';
const isDevEnv = runtimeConfig.public.appEnv === 'local';
type SpriteStatusMap = { [key: string]: SpriteStatus };
if (!useState(spriteStatusKey).value) {
  useState<SpriteStatusMap>(spriteStatusKey, () => ({}));
}

/**
 * Define the versions for the SVG sprites based on the folder names
 * This allows for easy mapping of sprites to their latest versions.
 **/
const spritesVersion: { [folder: string]: string } = {
  'learner-dashboard': 'learner-dashboard-v1',
  'teacher-dashboard': 'teacher-dashboard',
  'shared-dashboard': 'shared-dashboard'
};

/**
 * LIFECYCLE HOOKS
 * (functions that run at certain points in the component's lifecycle)
 */
// onMounted(() => {
if (process.client) {
  _loadSvgSprite();
}
// });

/**
 * PRIVATE FUNCTIONS
 *
 */

/**
 * Asynchronously loads an SVG sprite based on the provided props.
 * If the sprite is not already loaded or in the process of loading,
 * it makes an AJAX request to fetch the sprite and inserts it into the document.
 * Once the sprite is loaded, it updates the sprite status in the state.
 */
async function _loadSvgSprite() {
  const spriteStatusValue = useState<{ [key: string]: SpriteStatus }>(
    spriteStatusKey
  ).value;

  const folderSpriteStatus = spriteStatusValue?.[props.folder];

  // Construct the file path for the sprite based on the folder
  let spritePath = `/sprites/${spritesVersion[props.folder] ?? props.folder}.svg`;

  // If not in development environment, prepend the CDN URL
  if (!isDevEnv) {
    spritePath = `${runtimeConfig.public.cdnURL}${spritePath}`;
  }

  if (
    !folderSpriteStatus ||
    (folderSpriteStatus !== SpriteStatus.LOADING &&
      folderSpriteStatus !== SpriteStatus.LOADED)
  ) {
    // Set the sprite status to loading
    useState<{ [key: string]: string }>(spriteStatusKey).value[props.folder] =
      SpriteStatus.LOADING;

    // Load the sprite
    const ajax = new XMLHttpRequest();
    ajax.open('GET', spritePath, true);
    ajax.send();
    ajax.onload = function () {
      // Insert the sprite into the document
      const div = document.createElement('div');
      div.innerHTML = ajax.responseText;
      document.body.insertBefore(div, document.body.childNodes[0] || null);

      // Update the sprite status to loaded
      useState<{ [key: string]: string }>(spriteStatusKey).value[props.folder] =
        SpriteStatus.LOADED;
    };
  }
}
</script>
<style scoped lang="scss">
svg {
  height: 1em;
  width: 1em;
  margin-bottom: 0.125em;
  vertical-align: middle;
}
.spinner {
  svg {
    animation: spin 2s infinite linear !important;
    animation-name: spin !important;
  }
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
